import { faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Card } from 'src/components/Card';
import { Icon } from 'src/components/Icon';
import { Stack } from 'src/components/Stack';
import { Tooltip } from 'src/components/Tooltip';
import { Typography } from 'src/components/Typography';
import { getPaymentMethodIcon } from 'src/models/payment-methods/utils';

interface PaymentMethodSelectorProps {
  isGuestCheckout: boolean;
  paymentMethod: {
    paymentMethodType?: string;
    last4?: string;
    brand?: string;
    type?: string;
  };
  onPaymentMethodClick?: () => void;
}

export const PaymentMethodSelector = ({
  isGuestCheckout,
  paymentMethod,
  onPaymentMethodClick,
}: PaymentMethodSelectorProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'pointOfSale.cart',
  });

  const getPaymentMethodText = () => {
    if (paymentMethod.type === 'card_present') {
      return t('swipeCard');
    }
    return `${paymentMethod.last4} (${paymentMethod.brand})`;
  };

  return (
    <Card
      variant="outlined"
      onClick={!isGuestCheckout ? onPaymentMethodClick : undefined}
      sx={{
        px: 2,
        py: 1,
        borderColor: 'divider',
        cursor: !isGuestCheckout ? 'pointer' : 'not-allowed',
        width: '100%',
        bgcolor: 'background.level1',
        transition: 'box-shadow 0.2s',
        '&:hover': {
          boxShadow: !isGuestCheckout ? 3 : undefined,
        },
        borderRadius: 1,
        opacity: isGuestCheckout ? 0.5 : 1,
      }}
      elevation={0}
    >
      <Tooltip
        title={
          isGuestCheckout
            ? t('tooltip.guestCheckout')
            : t('tooltip.changePaymentMethod')
        }
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Icon
            size="medium"
            icon={getPaymentMethodIcon(
              paymentMethod.paymentMethodType || 'card'
            )}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 500,
                color: 'text.primary',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                textDecoration: !isGuestCheckout ? 'underline' : 'none',
                fontSize: 15,
              }}
              component="span"
            >
              {isGuestCheckout ? t('swipeCard') : getPaymentMethodText()}
            </Typography>
          </Box>
          <Icon
            icon={faChevronRight}
            size="small"
            sx={{ color: 'text.secondary' }}
          />
        </Stack>
      </Tooltip>
    </Card>
  );
};
